import { keepPreviousData } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import {
  Alert,
  Card,
  Col,
  Descriptions,
  Result,
  Row,
  Segmented,
  Space,
  Spin,
  Typography,
} from 'antd';
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  MinusOutlined,
} from '@ant-design/icons';
import {
  ResponsiveContainer,
  LineChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  Legend,
  Line,
} from 'recharts';
import { trpc } from '../../lib/api';
import { useAdminAccess } from '../../hooks/useAdminAccess';

const { Title, Text } = Typography;

const percentageFormatter = new Intl.NumberFormat('ja-JP', {
  style: 'percent',
  maximumFractionDigits: 1,
});

const currencyFormatter = new Intl.NumberFormat('ja-JP', {
  style: 'currency',
  currency: 'JPY',
  maximumFractionDigits: 0,
});

type SummaryCardFormat = 'percentage' | 'currency' | 'number';

type SummaryCard = {
  key: 'netRevenueRetention' | 'customerChurnRate' | 'revenueChurnRate' | 'monthlyRecurringRevenue';
  label: string;
  current: number;
  previous: number | null;
  delta: number | null;
  trend: 'up' | 'down' | 'flat';
  target?: number | null;
  format: SummaryCardFormat;
};

type SummaryResponse = {
  snapshotDate: string;
  cards: SummaryCard[];
  totals: {
    startingCustomers: number;
    newCustomers: number;
    churnedCustomers: number;
    activeCustomers: number;
    supportTicketCount: number;
  };
  revenue: {
    startingMrr: number;
    cancelledMrr: number;
    downgradeMrr: number;
    expansionMrr: number;
    activationVelocityDays: number;
    netRevenueRetention: number;
    revenueChurnRate: number;
    customerChurnRate: number;
    monthlyRecurringRevenue: number;
  };
};

type TrendResponse = {
  range: '30d' | '12m';
  points: Array<{
    date: string;
    netRevenueRetention: number;
    customerChurnRate: number;
    revenueChurnRate: number;
    activeCustomers: number;
    monthlyRecurringRevenue: number;
  }>;
};

type AnomalyMetric = 'customer_churn_rate' | 'revenue_churn_rate';

type AnomalyResponse = Array<{
  metric: AnomalyMetric;
  snapshotDate: string;
  value: number;
  threshold: number;
  zScore: number;
}>;

function formatValue(value: number, format: SummaryCardFormat): string {
  if (!Number.isFinite(value)) {
    return '—';
  }
  switch (format) {
    case 'percentage':
      return percentageFormatter.format(value);
    case 'currency':
      return currencyFormatter.format(value);
    default:
      return value.toLocaleString('ja-JP');
  }
}

function formatDelta(delta: number | null, format: SummaryCardFormat): string {
  if (delta === null || Number.isNaN(delta)) {
    return '—';
  }
  const sign = delta > 0 ? '+' : '';
  switch (format) {
    case 'percentage':
      return `${sign}${(delta * 100).toFixed(1)} pt`;
    case 'currency': {
      const formatted = currencyFormatter.format(Math.abs(delta));
      return delta >= 0 ? `+${formatted}` : `-${formatted}`;
    }
    default:
      return `${sign}${delta.toLocaleString('ja-JP')}`;
  }
}

function trendIcon(trend: SummaryCard['trend']) {
  switch (trend) {
    case 'up':
      return <ArrowUpOutlined style={{ color: '#3f8600' }} />;
    case 'down':
      return <ArrowDownOutlined style={{ color: '#cf1322' }} />;
    default:
      return <MinusOutlined style={{ color: '#8c8c8c' }} />;
  }
}

function trendColor(trend: SummaryCard['trend']): string {
  switch (trend) {
    case 'up':
      return '#3f8600';
    case 'down':
      return '#cf1322';
    default:
      return '#8c8c8c';
  }
}

function anomalyLabel(metric: AnomalyMetric): string {
  switch (metric) {
    case 'customer_churn_rate':
      return '顧客チャーン率';
    case 'revenue_churn_rate':
      return '売上チャーン率';
    default:
      return metric;
  }
}

export function AdminDashboard() {
  const { canViewKpiDashboard } = useAdminAccess();
  const [range, setRange] = useState<'30d' | '12m'>('30d');

  const summaryQuery = trpc.kpiAdmin.summary.useQuery({});
  const trendQuery = trpc.kpiAdmin.trend.useQuery({ range }, { placeholderData: keepPreviousData });
  const anomaliesQuery = trpc.kpiAdmin.anomalies.useQuery({});

  const summary = summaryQuery.data as SummaryResponse | undefined;
  const trend = trendQuery.data as TrendResponse | undefined;
  const anomalies = (anomaliesQuery.data as AnomalyResponse | undefined) ?? [];

  const chartData = useMemo(() => {
    if (!trend?.points) {
      return [];
    }
    return trend.points.map((point) => ({
      date: point.date.slice(5),
      netRevenueRetention: Number((point.netRevenueRetention * 100).toFixed(2)),
      customerChurnRate: Number((point.customerChurnRate * 100).toFixed(2)),
      revenueChurnRate: Number((point.revenueChurnRate * 100).toFixed(2)),
      monthlyRecurringRevenue: Math.round(point.monthlyRecurringRevenue),
    }));
  }, [trend]);

  const loading = summaryQuery.isLoading || trendQuery.isLoading;

  if (!canViewKpiDashboard) {
    return (
      <Result
        status="403"
        title="権限がありません"
        subTitle="KPIダッシュボードへのアクセス権限が付与されていません。管理者にお問い合わせください。"
      />
    );
  }

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Space direction="vertical" size={4} style={{ width: '100%' }}>
        <Title level={2} style={{ marginBottom: 0 }}>KPIダッシュボード</Title>
        <Text type="secondary">サブスクリプション事業の主要指標をモニタリングします。</Text>
      </Space>

      <Alert
        type="info"
        showIcon
        message="デイリー更新"
        description={`スナップショット日: ${summary?.snapshotDate ?? '—'}`}
      />

      {loading && <Spin size="large" />}

      {!loading && summary && (
        <>
          <Row gutter={[16, 16]}>
            {summary.cards.map((card) => (
              <Col xs={24} sm={12} lg={6} key={card.key}>
                <Card>
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <Text type="secondary">{card.label}</Text>
                    <Title level={3} style={{ margin: 0 }}>{formatValue(card.current, card.format)}</Title>
                    <Space size="small">
                      {trendIcon(card.trend)}
                      <Text style={{ color: trendColor(card.trend) }}>{formatDelta(card.delta, card.format)}</Text>
                    </Space>
                    {card.target ? (
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        目標: {formatValue(card.target, card.format)}
                      </Text>
                    ) : null}
                  </Space>
                </Card>
              </Col>
            ))}
          </Row>

          <Row gutter={[16, 16]}>
            <Col xs={24} lg={16}>
              <Card
                title="トレンド"
                extra={
                  <Segmented
                    value={range}
                    onChange={(value) => setRange(value as '30d' | '12m')}
                    options={[
                      { label: '直近30日', value: '30d' },
                      { label: '直近12ヶ月', value: '12m' },
                    ]}
                  />
                }
              >
                {chartData.length === 0 ? (
                  <Text type="secondary">トレンドデータがありません。</Text>
                ) : (
                  <ResponsiveContainer width="100%" height={320}>
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis yAxisId="left" tickFormatter={(value) => `${value}%`} width={70} />
                      <YAxis yAxisId="right" orientation="right" tickFormatter={(value) => currencyFormatter.format(value)} width={100} />
                      <RechartsTooltip
                        formatter={(value: number, name: string) => {
                          if (name === 'MRR') {
                            return [currencyFormatter.format(value), name];
                          }
                          return [`${value.toFixed(2)}%`, name];
                        }}
                      />
                      <Legend />
                      <Line yAxisId="left" type="monotone" dataKey="netRevenueRetention" name="NRR" stroke="#3f8600" dot={false} />
                      <Line yAxisId="left" type="monotone" dataKey="customerChurnRate" name="顧客チャーン率" stroke="#cf1322" dot={false} />
                      <Line yAxisId="left" type="monotone" dataKey="revenueChurnRate" name="売上チャーン率" stroke="#fa8c16" dot={false} />
                      <Line yAxisId="right" type="monotone" dataKey="monthlyRecurringRevenue" name="MRR" stroke="#1890ff" dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </Card>
            </Col>
            <Col xs={24} lg={8}>
              <Card title="サマリー">
                <Descriptions size="small" column={1} bordered>
                  <Descriptions.Item label="開始顧客数">
                    {summary.totals.startingCustomers.toLocaleString('ja-JP')}
                  </Descriptions.Item>
                  <Descriptions.Item label="新規顧客数">
                    {summary.totals.newCustomers.toLocaleString('ja-JP')}
                  </Descriptions.Item>
                  <Descriptions.Item label="解約顧客数">
                    {summary.totals.churnedCustomers.toLocaleString('ja-JP')}
                  </Descriptions.Item>
                  <Descriptions.Item label="稼働顧客数">
                    {summary.totals.activeCustomers.toLocaleString('ja-JP')}
                  </Descriptions.Item>
                  <Descriptions.Item label="サポートチケット">
                    {summary.totals.supportTicketCount.toLocaleString('ja-JP')}
                  </Descriptions.Item>
                </Descriptions>
              </Card>
            </Col>
          </Row>

          <Card title="MRR／リテンション内訳">
            <Descriptions size="small" column={2} bordered>
              <Descriptions.Item label="開始MRR">
                {currencyFormatter.format(summary.revenue.startingMrr)}
              </Descriptions.Item>
              <Descriptions.Item label="現在MRR">
                {currencyFormatter.format(summary.revenue.monthlyRecurringRevenue)}
              </Descriptions.Item>
              <Descriptions.Item label="解約MRR">
                {currencyFormatter.format(summary.revenue.cancelledMrr)}
              </Descriptions.Item>
              <Descriptions.Item label="ダウングレードMRR">
                {currencyFormatter.format(summary.revenue.downgradeMrr)}
              </Descriptions.Item>
              <Descriptions.Item label="アップセルMRR">
                {currencyFormatter.format(summary.revenue.expansionMrr)}
              </Descriptions.Item>
              <Descriptions.Item label="NRR">
                {percentageFormatter.format(summary.revenue.netRevenueRetention)}
              </Descriptions.Item>
              <Descriptions.Item label="売上チャーン率">
                {percentageFormatter.format(Math.max(summary.revenue.revenueChurnRate, 0))}
              </Descriptions.Item>
              <Descriptions.Item label="顧客チャーン率">
                {percentageFormatter.format(Math.max(summary.revenue.customerChurnRate, 0))}
              </Descriptions.Item>
              <Descriptions.Item label="オンボーディング中央値">
                {`${summary.revenue.activationVelocityDays.toFixed(1)} 日`}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          {anomalies.length > 0 && (
            <Alert
              type="warning"
              showIcon
              message="異常検知"
              description={
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  {anomalies.map((anomaly) => (
                    <li key={`${anomaly.metric}-${anomaly.snapshotDate}`}>
                      {`${anomaly.snapshotDate} ${anomalyLabel(anomaly.metric)}: ${(anomaly.value * 100).toFixed(2)}% (閾値 ${(anomaly.threshold * 100).toFixed(2)}%)`}
                    </li>
                  ))}
                </ul>
              }
            />
          )}
        </>
      )}

      {!loading && !summary && (
        <Alert type="warning" message="KPIデータが見つかりません" />
      )}
    </Space>
  );
}
